import React from 'react'
import {
  isNull,
  isEmpty,
  isFunction,
  identity,
  isUndefined,
  first,
  get
} from 'lodash'

import OverflowMenu from '@ublique/components/OverflowMenu'

import {
  columnPropsByType,
  ScenarioPermissions
} from 'pages/Scenario/constants'
import {
  cancelScenario,
  killScenario,
  removeScenario,
  resetScenario
} from 'pages/Scenario/actions'

import { openModal } from 'components/Modal/actions'
import {
  getScenarioColumns,
  getStandardColumnDefs
} from '../Manager/utils/table'
import { exportOutputs } from 'services/api/scenarios'

const onRemoveScenario = ({ data }) => {
  openModal('deletingScenario', {
    onSubmit: (items) => removeScenario(first(items)),
    selectedRows: [{ ...data }],
    selectAll: false
  })
}

const onCancelScenario = ({ data }) => {
  openModal('cancelingScenario', {
    onSubmit: (items) => cancelScenario({ scenario: first(items) }),
    selectedRows: [{ ...data }],
    selectAll: false
  })
}
const onKillScenario = ({ data }) => {
  openModal('killingScenario', {
    onSubmit: (items) => killScenario({ scenario: first(items) }),
    selectedRows: [{ ...data }],
    selectAll: false
  })
}

const onWarningScenario = ({ data }) => {
  openModal('warningScenario', {
    scenario: data
  })
}

const onTrendScenario = ({ data, component }) => {
  openModal('progressScenario', {
    scenario: data,
    component
  })
}
const onDownloadOutputScenario = async ({ data }) => {
  const body = {
    planId: null,
    scenarioCodeList: [data.id],
    zip: true,
    lang: localStorage.getItem('i18n-lang')
  }
  await exportOutputs(body)
}

const ScenarioTableMenuActions = {
  resetScenario: {
    permission: ScenarioPermissions.UPDATE,
    menuAction: resetScenario
  },
  deleteScenario: {
    permission: ScenarioPermissions.DELETE,
    menuAction: onRemoveScenario
  },
  cancelScenario: {
    permission: ScenarioPermissions.READ,
    menuAction: onCancelScenario
  },
  killScenario: {
    permission: ScenarioPermissions.READ,
    menuAction: onKillScenario
  },
  warningScenario: {
    permission: ScenarioPermissions.READ,
    menuAction: onWarningScenario
  },
  progressScenario: {
    permission: ScenarioPermissions.READ,
    menuAction: onTrendScenario
  },
  downloadOutputScenario: {
    permission: ScenarioPermissions.READ,
    menuAction: onDownloadOutputScenario
  }
}

export const commonColumnProps = {
  sortable: true,
  resizable: true,
  suppressMovable: true,
  filter: 'agTextColumnFilter'
}

export const getFilterParams = (t) => ({
  textFormatter: (value) => t(value).toLowerCase()
})

export const createDynamicColumn = ({ t, field, type, label, position }) => ({
  ...commonColumnProps,
  field,
  position,
  colId: field,
  headerName: t(`Wizard:Scenario.${label}`),
  filterParams: getFilterParams(t),
  // It is spreaded after filterParams
  // In this way, if there is not a filterParams defined, it will use
  // the default up here that need the translation function
  ...(columnPropsByType[type] ?? {})
})

export const tableParser = ({ rowData }) =>
  rowData.map((item) => rowParser({ item }))

export const rowParser = ({ item }) => ({
  ...item,
  type: item.typeValue ?? item.type?.value ?? '',
  progress: {
    status: item.status
  }
})

export const buildMenu = ({
  config,
  data,
  gridApi,
  history,
  modelPermissions,
  showOutputPermission
}) => {
  const { id, scenarioPermission: rowPermissions } = data
  const getMenuAction = ({ action, component, ...props }) => {
    // If the function came from the config
    if (isFunction(action)) {
      return action(props)
    }

    // If the action chosen came from platfrom
    const { permission, menuAction } = ScenarioTableMenuActions[action] ?? {}

    if (
      menuAction &&
      rowPermissions[permission] &&
      modelPermissions[permission]
    ) {
      return menuAction({ ...props, component })
    }

    // Default case: action not found
    return identity(props)
  }

  const checkDisabled = ({ permission, action, forcePermission }) => {
    if (forcePermission) {
      // forced by actionConfig
      return false
    }

    if (!showOutputPermission) {
      return true
    }

    let rowPerm = permission

    if (isUndefined(permission)) {
      const { permission: platPermission } =
        ScenarioTableMenuActions[action] ?? {}

      rowPerm = platPermission
    }

    return !rowPermissions[rowPerm] || !modelPermissions[rowPerm]
  }

  return (
    <OverflowMenu ariaLabel='Menu' direction='top' flipped>
      {!isEmpty(config) &&
        config.map(
          ({ title, action, permission, component, forcePermission }) => (
            <OverflowMenu.Item
              key={`${id}-${title}`}
              itemText={title}
              disabled={checkDisabled({
                permission,
                action,
                forcePermission
              })}
              onClick={() =>
                getMenuAction({ action, component, data, gridApi, history })
              }
            />
          )
        )}
    </OverflowMenu>
  )
}

const padTime = (time) => `${time}`.padStart(2, '0')

export const calculateTimeLeft = (futureEpoch) => {
  let timeLeft = {}
  const difference = futureEpoch - Date.now()

  if (difference > 0) {
    timeLeft = {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: padTime(Math.floor((difference / (1000 * 60 * 60)) % 24)),
      minutes: padTime(Math.floor((difference / 1000 / 60) % 60)),
      seconds: padTime(Math.floor((difference / 1000) % 60))
    }
  }

  return { difference, timeLeft }
}

export const formatTimeLeft = ({ timeLeft, LABEL_DAYS }) => {
  let stringDate = isNull(timeLeft) ? '' : '00:00:00'

  if (!isEmpty(timeLeft)) {
    const { days, hours, minutes, seconds } = timeLeft

    stringDate = days > 0 ? `${days} ${LABEL_DAYS} ` : ''
    stringDate += `${hours}:${minutes}:${seconds}`
  }

  return stringDate
}

export const getTableColumns = ({ t, rows, additionalColumns }) => {
  const colPermissions = get(first(rows), 'scenarioPermission.fields', {})
  // const {
  //   scenarioPermission: { fields: colPermissions }
  // } = first(rows)

  let scenarioColumns = [...getScenarioColumns(t)].filter(
    (col) => colPermissions[col.field]
  )

  const allowedAdditionaColumns = additionalColumns.filter(
    (col) => colPermissions[col.field]
  )

  for (const customColumn of allowedAdditionaColumns) {
    const col = createDynamicColumn({ ...customColumn, t })
    scenarioColumns = switchPosition(scenarioColumns, col)
  }

  return [...getStandardColumnDefs(t), ...scenarioColumns]
}

const switchPosition = (columns, newColumn) => {
  const { position } = newColumn

  if (!position || position > columns.length) return [...columns, newColumn]
  if (position === 1) return [newColumn, ...columns]

  return [
    ...columns.slice(0, position - 1),
    newColumn,
    columns[position - 1],
    ...columns.slice(position)
  ]
}
